// web/src/services/auth.ts
// Web auth service — mirrors mobile auth using same Cognito pool
// Uses Amplify v6 tree-shakeable imports (~45KB gzipped)

import { Amplify } from 'aws-amplify';
import {
  signIn, signUp, signOut, confirmSignUp,
  getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword,
  updateUserAttributes, fetchUserAttributes,
} from 'aws-amplify/auth';

// Configure Amplify — same Cognito pool as mobile app
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (userPoolId && clientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
      },
    },
  });
}

export interface AuthState {
  isAuthenticated: boolean;
  user: { userId: string; email: string } | null;
  isLoading: boolean;
}

export const WebAuthService = {
  isConfigured(): boolean {
    return !!(userPoolId && clientId);
  },

  async signUp(email: string, password: string) {
    return signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
  },

  async confirmSignUp(email: string, code: string) {
    return confirmSignUp({ username: email, confirmationCode: code });
  },

  async signIn(email: string, password: string) {
    return signIn({ username: email, password });
  },

  async signOut() {
    try {
      await signOut();
    } catch (err) {
      console.error('[FRAME Auth] Sign out error:', err);
    }
    // Clear sensitive session data from localStorage
    try {
      const keysToRemove = Object.keys(localStorage).filter(k =>
        k.startsWith('frame_chat_') || k === 'frame_display_name'
      );
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch { /* storage access may fail in private browsing */ }
  },

  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  },

  async getSession() {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      // Validate token isn't expired; force refresh if it is
      const exp = session?.tokens?.idToken?.payload?.exp;
      if (exp && typeof exp === 'number' && exp * 1000 < Date.now()) {
        return await fetchAuthSession({ forceRefresh: true });
      }
      return session;
    } catch {
      return null;
    }
  },

  async resetPassword(email: string) {
    return resetPassword({ username: email });
  },

  async confirmResetPassword(email: string, code: string, newPassword: string) {
    return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
  },

  async setDisplayName(name: string) {
    await updateUserAttributes({ userAttributes: { preferred_username: name } });
    localStorage.setItem('frame_display_name', name);
  },

  async getDisplayName(): Promise<string | null> {
    // Fast path: localStorage cache
    const cached = localStorage.getItem('frame_display_name');
    if (cached) return cached;
    try {
      const attrs = await fetchUserAttributes();
      const name = attrs.preferred_username || null;
      if (name) localStorage.setItem('frame_display_name', name);
      return name;
    } catch {
      return null;
    }
  },

  clearDisplayNameCache() {
    localStorage.removeItem('frame_display_name');
  },
};
